import { z } from "zod";
/**
 * **The stat block payload — architecture §16g A75 + Ivy §6.1/§6.2/§6.6.**
 *
 * The server builds an ordered array of slots; the client renders the array and
 * never a named stat. Every rule about *which* slots exist, in what order, and
 * when one is absent lives in `@wr/stats`'s `slots.ts` — this file is the shape
 * those decisions travel in.
 *
 * ── The three properties that are LOCKED about this shape ────────────────────
 *
 * 1. **Three kinds, and only three** (Ivy §6.1). `emphasis` on `row` is one
 *    optional field on an existing kind, explicitly *not* a fourth kind
 *    (Ivy §6.6.3). A stat that none of the three can carry is a gap in Ivy's
 *    document and goes back to her.
 * 2. **Values arrive as numbers and the client formats them** (Ivy §6.1). Win
 *    rate is rounded once, on the server, so S8's admin view cannot disagree
 *    with the player's view.
 * 3. **Presence is the payload's job.** A conditional stat is *omitted from the
 *    array*, never sent with a flag — `current_win_streak` below 2 and
 *    `win_rate` below 10 decided games simply are not there (A75), and from
 *    T-271 **any slot whose value is 0** is not there either (Sana §8.5 C1).
 *    A client that renders the array it is given cannot produce a "Wins 0"
 *    plate, because the array cannot carry one.
 *
 * `label` / `text` are `packages/i18n` keys, not strings. They are typed as
 * `string` here rather than as `StringKey` because `@wr/contracts` is zod-only
 * and may not depend on `@wr/i18n` (see the package header); the client resolves
 * them through `t()`, and the api's `routes/profile.test.ts` — the one tier that
 * sees both packages — asserts that every key this file can produce resolves in
 * the catalogue.
 */
/** Ivy §6.2's formats. `percent` renders whole numbers — no decimal. */
export const STAT_FORMATS = ["count", "percent", "streak"];
export const statFormatSchema = z.enum(STAT_FORMATS);
/**
 * **Ivy §6.6.3.** Permitted on `row` only, meaningless on `headline`, forbidden
 * on `note`. The server sets it on `current_win_streak` and nothing else, so the
 * client renders `slot.emphasis === "celebrate"` and performs **no value
 * comparison of its own** — the ≥ 2 gate exists in exactly one place.
 */
export const STAT_EMPHASIS = ["celebrate"];
export const statEmphasisSchema = z.enum(STAT_EMPHASIS);
const statSlotBase = {
    /** Stable, and what `data-stat` carries — Quinn asserts a slot without asserting its label. */
    key: z.string().min(1),
    /** A `packages/i18n` key. Never a rendered string. */
    label: z.string().min(1),
    value: z.number().int(),
    format: statFormatSchema,
};
/** Max 3 per scope, and always the same keys for every player (Ivy §6.6.2). */
export const statHeadlineSlotSchema = z.object({ kind: z.literal("headline"), ...statSlotBase });
export const statRowSlotSchema = z.object({
    kind: z.literal("row"),
    ...statSlotBase,
    emphasis: statEmphasisSchema.optional(),
});
/**
 * A derived aside — Juno Ruling #9 §4's named denominator, and A75's honest line
 * below the win-rate gate. **Never the sole carrier of a number a player would
 * look for** (Ivy §6.2), which is why it has `text` and no `value`.
 */
export const statNoteSlotSchema = z.object({
    kind: z.literal("note"),
    key: z.string().min(1),
    text: z.string().min(1),
    params: z.record(z.string(), z.number()).optional(),
});
export const statSlotSchema = z.discriminatedUnion("kind", [
    statHeadlineSlotSchema,
    statRowSlotSchema,
    statNoteSlotSchema,
]);
/**
 * One game type's record. Sana §2.8: the block is labelled by game type from day
 * one, even while there is only one — an unlabelled block retroactively lies
 * about what every number ever meant the day a second type activates.
 */
export const statScopeSchema = z.object({
    gameTypeId: z.string().uuid(),
    gameTypeName: z.string(),
    slots: z.array(statSlotSchema),
});
/**
 * `GET /api/v1/players/me/stats` — **A72 source 3**.
 *
 * A scope with zero settled games is **absent, not zeroed** (Ivy §6.4, Sana
 * §4.1), so an empty array IS the empty state: there is no `empty` flag to
 * disagree with the array's length.
 *
 * **No balance here** (A72 LOCKED item 1) and nothing branches on `role`
 * (Sana §4.5 — guests get the full array).
 */
export const playerStatsResponseSchema = z.object({ scopes: z.array(statScopeSchema) });
// ---------------------------------------------------------------------------
// slot keys
// ---------------------------------------------------------------------------
/**
 * Every `key` a slot may carry, spelled once.
 *
 * The client never switches on these — it renders the array — but Quinn's probes
 * and the client's `data-stat` attribute both need the vocabulary to be fixed,
 * and a typo'd key in the builder is otherwise invisible until somebody reads a
 * screenshot.
 */
export const STAT_KEYS = {
    gamesPlayed: "games_played",
    wins: "wins",
    scoreTotal: "score_total",
    bestGameScore: "best_game_score",
    longestWinStreak: "longest_win_streak",
    currentWinStreak: "current_win_streak",
    winRate: "win_rate",
    /** Juno R9 §4: the denominator note that travels **with** a rendered win rate. */
    winRateNote: "win_rate_note",
    /**
     * `winRatePending: "win_rate_pending"` was **retired here, 2026-08-03
     * (T-271)**, and is not to be reinstated without citing Sana §8.5.
     *
     * **Sana §8.5 C2, BINDING — and it amends her own §2.7:** *"Below 10 decided
     * games in a type, the win-rate slot renders nothing at all. It appears on
     * the 10th decided game, exactly as the streak chip appears on the 2nd."*
     * §2.7 had permitted a sub-gate line in the honest form *"a few games isn't
     * enough to tell"*; A84's practice game makes that line **false** for a
     * bot-heavy player, who may have 40 games played and 2 decided. *"Rather than
     * write a second string for a second reason, delete the surface."*
     *
     * It also removes the only place the profile block would have been tempted to
     * explain its own denominator, which Ivy §6.3 LOCKS it out of anyway.
     *
     * The key is recorded rather than silently deleted because a copy job was
     * cancelled by it: Theo's §7 sub-gate string brief is **superseded, not
     * outstanding** (Sana §8.8, to Theo: *"the §7 sub-gate win-rate string is
     * cancelled"*). Its i18n key `profile.stats.winRate.pending` carries the same
     * note in `packages/i18n/src/en.ts`.
     */
};
// ---------------------------------------------------------------------------
// the per-engine label registry (A70)
// ---------------------------------------------------------------------------
/**
 * **A70 — the label is per-engine; the column is not.**
 *
 * `player_stats.score_total` and `best_game_score` are generic platform counters
 * (`Σ round_scores.points` and its per-room max). What they are *called* depends
 * on the engine: Word Search scores +1 per accepted claim, so its total is
 * Sana §2.3's "words found" — the same integer with two names.
 *
 * **Partial on purpose** (A54's posture): an unknown `game_types.engine_key` is
 * a version-skew state, not an error, and it falls back to the generic labels
 * below rather than rendering a blank. Adding an engine adds a row here; it does
 * not add a column anywhere.
 */
export const ENGINE_STAT_LABELS = {
    word_search: {
        scoreTotal: "stats.label.word_search.score_total",
        bestGameScore: "stats.label.word_search.best_game_score",
    },
};
export const GENERIC_STAT_LABELS = {
    scoreTotal: "stats.label.score_total",
    bestGameScore: "stats.label.best_game_score",
};
/** The i18n key for this engine's mastery counter (Ivy §6.6's third plate). */
export function scoreTotalLabelKey(engineKey) {
    return ENGINE_STAT_LABELS[engineKey]?.scoreTotal ?? GENERIC_STAT_LABELS.scoreTotal;
}
/** The i18n key for this engine's personal best (Sana §2.4). */
export function bestGameScoreLabelKey(engineKey) {
    return ENGINE_STAT_LABELS[engineKey]?.bestGameScore ?? GENERIC_STAT_LABELS.bestGameScore;
}
/** The engine-independent labels. Same shape as above so the builder reads uniformly. */
export const STAT_LABELS = {
    gamesPlayed: "stats.label.games_played",
    wins: "stats.label.wins",
    longestWinStreak: "stats.label.longest_win_streak",
    currentWinStreak: "stats.label.current_win_streak",
    winRate: "stats.label.win_rate",
};
/**
 * The **one** copy slot the win rate owns.
 *
 * It was two. **`winRatePending` was retired 2026-08-03 (T-271) under Sana §8.5
 * C2** — below the gate the win-rate slot renders nothing at all, so there is
 * no second condition left for a second string to answer. See the deck note on
 * the retired `STAT_KEYS.winRatePending` above; the i18n key
 * `profile.stats.winRate.pending` was retired in the same change.
 *
 * **Ratified and re-cased by Theo, 2026-08-02 (T-260).** `winrate` → `winRate`
 * is his rename — mechanical, no semantic change. The denominator note's *text*
 * is his ("Ties aren't counted."). Changing the string is a `packages/i18n`
 * edit and touches nothing here.
 */
export const STAT_NOTE_KEYS = {
    /** Juno R9 §4, BINDING: the denominator is named wherever the rate is shown. */
    winRateDenominator: "profile.stats.winRate.note",
};
//# sourceMappingURL=stats.js.map